import { useReducer, useEffect, useMemo, useCallback, useRef } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { databasePromise, getAuthAsync, appCheckReady } from "configuration";
import { dealService } from "services";
import { logAnalyticsEvent } from "services/logAnalyticsEvent";

const initialState = {
  deals: [],
  loading: true,
  error: null,
};

const dealsReducer = (state, action) => {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, deals: action.payload, loading: false, error: null };
    case "FETCH_ERROR":
      return { ...state, deals: [], loading: false, error: action.payload };
    default:
      return state;
  }
};

const hhmmToMinutes = (time) => {
  if (!time || typeof time !== "string") return 0;
  const [hours, minutes] = time.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

export const useDeals = (
  sortBy = "closestTime",
  filterDay = "all",
  searchQuery = "",
  filters = {},
) => {
  const [state, dispatch] = useReducer(dealsReducer, initialState);

  const likeDeal = useCallback(async (dealId) => {
    try {
      const auth = await getAuthAsync();
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error("User must be signed in");
      const db = await databasePromise;
      await updateDoc(doc(db, "deals", dealId), { likedBy: arrayUnion(uid) });
    } catch (error) {
      console.error("Error liking deal:", error);
    }
  }, []);

  const unlikeDeal = useCallback(async (dealId) => {
    try {
      const auth = await getAuthAsync();
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error("User must be signed in");
      const db = await databasePromise;
      await updateDoc(doc(db, "deals", dealId), { likedBy: arrayRemove(uid) });
    } catch (error) {
      console.error("Error unliking deal:", error);
    }
  }, []);

  // Stable ref so the effect doesn't re-run when filters object identity changes
  const restaurantIdRef = useRef(filters.restaurantId);
  const isOwnerRef = useRef(filters.isOwner);
  restaurantIdRef.current = filters.restaurantId;
  isOwnerRef.current = filters.isOwner;

  useEffect(() => {
    dispatch({ type: "FETCH_START" });
    let unsubscribe;
    let cancelled = false;

    // Snapshot the filter values at effect-run time
    const restaurantId = restaurantIdRef.current;
    const isOwner = isOwnerRef.current;

    // BUG-F01: push the most selective filters to the server query
    // rather than fetching the entire deals collection client-side.
    const excludedStatuses = ["rejected", "disabled", "pendingDelete", "deleted"];

    Promise.all([appCheckReady, databasePromise]).then(([, db]) => {
      if (cancelled) return;

      const constraints = [];

      if (restaurantId) {
        // Restaurant-scoped view: filter by restaurantId server-side
        constraints.push(where("restaurantId", "==", restaurantId));
        if (!isOwner) {
          // Public view: also exclude non-visible statuses server-side
          constraints.push(where("status", "not-in", excludedStatuses));
        } else {
          // Owner view: show everything except hard-deleted
          constraints.push(where("status", "!=", "deleted"));
        }
      } else {
        // Global feed: exclude non-visible statuses server-side
        constraints.push(where("status", "not-in", excludedStatuses));
      }

      const dealsQuery = query(collection(db, "deals"), ...constraints);

      unsubscribe = onSnapshot(
        dealsQuery,
        (snapshot) => {
          if (cancelled) return;
          try {
            const deals = snapshot.docs.map((dealDoc) => ({
              id: dealDoc.id,
              ...dealDoc.data(),
            }));
            dispatch({ type: "FETCH_SUCCESS", payload: deals });
          } catch (error) {
            console.error("Error processing deals:", error);
            dispatch({ type: "FETCH_ERROR", payload: error.message });
          }
        },
        (error) => {
          if (cancelled) return;
          console.error("[useDeals] Snapshot error:", error);
          dispatch({ type: "FETCH_ERROR", payload: error.message });
        }
      );
    }).catch((err) => {
      if (cancelled) return;
      console.error("[useDeals] Failed to initialize:", err);
      dispatch({ type: "FETCH_ERROR", payload: err.message });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  // Re-run when restaurantId or isOwner changes — these change the query shape
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.restaurantId, filters.isOwner]);

  // 1. Day filter
  const dayFilteredDeals = useMemo(() => {
    if (filterDay === "all") return state.deals;
    return state.deals.filter(
      (deal) => deal.daysOfTheWeek && deal.daysOfTheWeek.includes(filterDay)
    );
  }, [state.deals, filterDay]);

  // 2. Search filter
  const searchFilteredDeals = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === "") return dayFilteredDeals;
    const q = searchQuery.toLowerCase().trim();
    return dayFilteredDeals.filter(
      (deal) =>
        deal.title?.toLowerCase().includes(q) ||
        deal.restaurantName?.toLowerCase().includes(q) ||
        deal.description?.toLowerCase().includes(q) ||
        deal.categories?.some((cat) => cat.toLowerCase().includes(q))
    );
  }, [dayFilteredDeals, searchQuery]);

  // 3. Status + restaurant filter
  const statusFilteredDeals = useMemo(() => {
    const excludedStatuses = ["pendingCreate", "rejected", "disabled", "pendingDelete", "deleted"];

    if (filters.restaurantId) {
      if (filters.isOwner) {
        return searchFilteredDeals.filter(
          (deal) => deal.restaurantId === filters.restaurantId && deal.status !== "deleted"
        );
      }
      return searchFilteredDeals
        .filter((deal) => deal.restaurantId === filters.restaurantId)
        .filter((deal) => !excludedStatuses.includes(deal.status))
        .filter((deal) => deal.restaurantStatus !== "pendingDelete")
        .filter((deal) => deal.restaurantStatus !== "disabled");
    }

    return searchFilteredDeals
      .filter((deal) => !excludedStatuses.includes(deal.status))
      .filter((deal) => deal.restaurantStatus !== "pendingDelete")
      .filter((deal) => deal.restaurantStatus !== "disabled")
      .filter((deal) => !deal.restaurantTemporarilyClosed);
  }, [searchFilteredDeals, filters.restaurantId, filters.isOwner]);

  // 4. Advanced filters + sort
  const processedDeals = useMemo(() => {
    let filtered = statusFilteredDeals;

    if (filters.dealIds?.length > 0) {
      filtered = filtered.filter((deal) => filters.dealIds.includes(deal.id));
    }

    if (filters.isTakeaway) {
      filtered = filtered.filter((deal) => deal.isTakeaway === true);
    }

    if (filters.isHappeningNow) {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      filtered = filtered.filter((deal) => {
        if (!Array.isArray(deal.daysOfTheWeek) || !deal.daysOfTheWeek.includes(filterDay)) return false;
        const startMinutes = hhmmToMinutes(deal.timeStart || "00:00");
        const endMinutes = hhmmToMinutes(deal.timeEnd || "23:59");
        return startMinutes <= endMinutes
          ? currentMinutes >= startMinutes && currentMinutes <= endMinutes
          : currentMinutes >= startMinutes || currentMinutes <= endMinutes;
      });
    }

    if (filters.categories?.length > 0) {
      filtered = filtered.filter((deal) => {
        if (!Array.isArray(deal.categories)) return false;
        return filters.categories
          .map((c) => c.toLowerCase())
          .every((cat) => deal.categories.map((c) => c.toLowerCase()).includes(cat));
      });
    }

    if (filters.dietaryPreferences?.length > 0) {
      filtered = filtered.filter((deal) => {
        if (!Array.isArray(deal.dietaryPreferences)) return false;
        return filters.dietaryPreferences.every((dp) => deal.dietaryPreferences.includes(dp));
      });
    }

    if (filters.priceMinimum !== undefined && filters.priceMaximum !== undefined) {
      filtered = filtered.filter((deal) => {
        const price = typeof deal.price === "number" ? deal.price : 0;
        return price >= filters.priceMinimum && price <= filters.priceMaximum;
      });
    }

    if (filters.availableTimeMinimum && filters.availableTimeMaximum) {
      const filterStart = hhmmToMinutes(filters.availableTimeMinimum);
      const filterEnd = hhmmToMinutes(filters.availableTimeMaximum);
      filtered = filtered.filter((deal) => {
        if (!deal.timeStart || !deal.timeEnd) return true;
        return hhmmToMinutes(deal.timeStart) <= filterEnd && hhmmToMinutes(deal.timeEnd) >= filterStart;
      });
    }

    return sortBy && sortBy !== "default"
      ? dealService.sortDeals(filtered, sortBy)
      : filtered;
  }, [statusFilteredDeals, sortBy, filterDay, filters]);

  // Fire search_performed when the user has typed a query and results settle.
  // Debounced to 800 ms so we log after the user pauses, not on every keystroke.
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const processedCountRef = useRef(processedDeals.length);
  processedCountRef.current = processedDeals.length;

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    const timer = setTimeout(() => {
      logAnalyticsEvent("search_performed", {
        search_term: trimmed,
        result_count: processedCountRef.current,
      });
    }, 800);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return {
    deals: processedDeals,
    loading: state.loading,
    error: state.error,
    likeDeal,
    unlikeDeal,
  };
};
